import {
  useAddFullProductKitToCartMutation,
  useAddProductKitToCartMutation,
  useAddProductVariantToCartMutation,
  useAddProductVariantToWholesaleCartMutation,
  useCartUpdateProductVariantMutation,
} from '@graphql/mutations/__generated__/product-reflection.generated'
import { useDeleteCartLineMutation } from '@graphql/mutations/__generated__/cart.generated'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'
import { useReactiveVar } from '@apollo/client'
import { useVariantKitDataLazyQuery } from '@graphql/queries/__generated__/product-variant-kit.generated'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import { useQueryParams } from 'use-query-params'
import { GlobalDialogFormsParams } from '@components/GlobalDialogForms'
import { MetaWrapper } from '../../components/Meta'
import VariantKitPanel from '../../components/Product/VariantKitPanel/VariantKitPanel'
import useGlobalLoader from '../../hooks/useGlobalLoader'
import { formErrorsToString, mutationWarningsToMessages } from '../../misc/utils'
import { DataLayerItem, googlePushProductAddToCart, googlePushViewItem } from '../../utils/dataLayer'
import { syncCartCache } from '../../utils/cartCache'
import Page404 from '../Page404'
import ProductContext, { IProductContext } from './context/ProductContext'
import Page from './Page'
import { extractReflectionMeta, getAttributesFromProduct } from './utils'
import { useReflectionFullInfoQuery } from '@graphql/queries/__generated__/reflection.generated'
import { maybe } from '../../utils/misc'
import {
  ProductVariantFullInfoQueryResult,
  useProductVariantFullInfoLazyQuery,
} from '@graphql/queries/__generated__/product-variant.generated'
import { downloadFile } from '../../utils/order'
import { RecentlyViewedContext } from '@components/RecentlyViewed/RecentlyViewedProvider'
import { MAX_PRODUCT_QTY, MAX_WHOLESALE_PRODUCT_QTY, SITE_NAME } from '../../../constants'
import { CartTypeEnum, OutOfStockStatusEnum } from '../../../generated/gqlTypes'
import { isWholesaleModeVar } from '../../../apolloClient'
import { useTranslation } from 'react-i18next'

type SelectedVariant = ProductVariantFullInfoQueryResult['data']['productVariant']

// Wholesale mode: the variant has one line in the wholesale cart, whose quantity the form sets (adding replaces it).
// Stock isn't tracked for sale (IN) or can be backordered: only the hard ceiling applies (as in WholesaleSelection).
const getWholesaleMaxQuantity = (variant: SelectedVariant) => {
  const isUnlimited =
    variant.outOfStockStatus === OutOfStockStatusEnum.In || variant.outOfStockStatus === OutOfStockStatusEnum.Backorder
  return isUnlimited
    ? MAX_WHOLESALE_PRODUCT_QTY
    : Math.min(MAX_WHOLESALE_PRODUCT_QTY, Math.max(variant.stockQuantity, 1))
}

const View: React.FC<{
  isModal?: boolean
  initialId?: number | string
  setIsOpenProductDialog?({}: { pk: number; slug: string }): void
}> = ({ isModal, initialId, setIsOpenProductDialog }) => {
  const { t } = useTranslation()
  const { addId: addStorageViewedId } = useContext(RecentlyViewedContext)
  const { enqueueSnackbar } = useSnackbar()
  const [, setParams] = useQueryParams(GlobalDialogFormsParams)
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const cartType = isWholesaleMode ? CartTypeEnum.Wholesale : CartTypeEnum.Default
  const [selectedVariant, setSelectedVariant] = useState<SelectedVariant | null>(null)
  const [removing, setRemoving] = useState(false)
  const [attributes, setAttributes] = useState<
    {
      name: string
      values: string[]
    }[]
  >([])
  const [selectedAttrs, setSelectedAttrs] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [kitLoading, setKitLoading] = useState(false)
  const [addedVariant, setAddedVariant] = useState<IProductContext['lastAddedVariantInfo']>(null)
  const [kitFormData, setKitFormData] = useState(null)
  const [isOpenKitPanel, setIsOpenKitPanel] = useState(false)
  const { setLoading } = useGlobalLoader()
  const [localLoading, setLocalLoading] = useState(false)
  const { id } = useParams<{ id: string }>()

  const dataUser = useMeQuery()
  const reflectionQueryResult = useReflectionFullInfoQuery({
    variables: { id: String(initialId || id), wholesale: isWholesaleMode },
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      const props = maybe(() => data.reflectionDetails.reflection.product.variantsProperties, [])
      const attrs = getAttributesFromProduct(props)
      setAttributes(attrs)
      const selectedAttrs = {}
      if (attrs.length > 0) {
        attrs.forEach(attr => {
          if (attr.values.length === 1) {
            selectedAttrs[attr.name] = attr.values[0]
          }
        })
      } else if (reflectionQueryResult.data?.reflectionDetails?.reflection && attrs.length === 0) {
        matchVariantFromSelection({})
      }
      setSelectedAttrs(_.isEmpty(selectedAttrs) ? null : selectedAttrs)
      setQuantity(1)
      addStorageViewedId(data.reflectionDetails.reflection.pk)
      const reflection = data.reflectionDetails.reflection
      if (reflection?.product) {
        const price = reflection.product.priceRange.discount.min
        const items: DataLayerItem[] = [
          {
            item_id: reflection.product.sku,
            item_name: reflection.name,
            index: 0,
            quantity: 1,
            price: price,
          },
        ]
        googlePushViewItem({ currency: reflection.product.priceRange.currency, value: price, items })
      }
    },
    onError: error => {
      enqueueSnackbar(error.message, { variant: 'error' })
      setSelectedVariant(null)
    },
  })
  const [loadProductVariant, productVariantQueryResult] = useProductVariantFullInfoLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      setSelectedVariant(data?.productVariant)
      // Wholesale mode starts from the quantity already in the cart, which the form then updates.
      setQuantity((isWholesaleMode && data?.productVariant?.cartQuantity) || 1)
      setLocalLoading(false)
    },
    onError: error => {
      enqueueSnackbar(error.message, { variant: 'error' })
      setSelectedVariant(null)
      setQuantity(1)
      setLocalLoading(false)
    },
  })

  const [loadKitData, dataKitQuery] = useVariantKitDataLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      const res = data.productVariant.customizeKitData
      setKitFormData(res)
      setIsOpenKitPanel(true)
    },
    onError: error => {
      enqueueSnackbar(error.message, {
        variant: 'error',
      })
    },
  })
  // The cart comes back in the payload (syncCartCache also makes mounted shipping-discount queries refetch);
  // only the line list needs a refetch.
  const [addVariantToBag] = useAddProductVariantToCartMutation({
    refetchQueries: ['CartLines'],
    awaitRefetchQueries: true,
    update: (cache, { data }) => syncCartCache(cache, data?.addVariantToCart.cart),
  })
  const [addFullKitToBag] = useAddFullProductKitToCartMutation({
    refetchQueries: ['CartLines'],
    awaitRefetchQueries: true,
    update: (cache, { data }) => syncCartCache(cache, data?.addFullKitToCart.cart),
    onCompleted: data =>
      mutationWarningsToMessages(data.addFullKitToCart.warnings).forEach(message =>
        enqueueSnackbar(message, { variant: 'warning' })
      ),
  })
  const [addKitToBag] = useAddProductKitToCartMutation({
    refetchQueries: ['CartLines'],
    awaitRefetchQueries: true,
    update: (cache, { data }) => syncCartCache(cache, data?.addKitToCart.cart),
    onCompleted: data =>
      mutationWarningsToMessages(data.addKitToCart.warnings).forEach(message =>
        enqueueSnackbar(message, { variant: 'warning' })
      ),
  })
  // Wholesale cart: adding sets the variant line's quantity, and a kit already in the cart only changes its quantity
  // (adding it again would replace its parts). After every change the variant is reloaded for its cart quantity.
  const [addVariantToWholesaleCart] = useAddProductVariantToWholesaleCartMutation({
    refetchQueries: ['CartLines'],
    awaitRefetchQueries: true,
    update: (cache, { data }) => syncCartCache(cache, data?.addVariantToCart.cart),
  })
  const [updateWholesaleCartLine] = useCartUpdateProductVariantMutation({
    refetchQueries: ['CartLines'],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      if (!data?.cartUpdateVariant.errors.length) syncCartCache(cache, data?.cartUpdateVariant.cart)
    },
  })
  const [deleteCartLine] = useDeleteCartLineMutation({
    refetchQueries: ['CartLines'],
    awaitRefetchQueries: true,
    update: (cache, { data }) => syncCartCache(cache, data?.deleteCartLine.cart),
  })
  // Reset the form only when switching to another product (or the mode, whose variants differ). Keying this on the
  // query data would also fire after the refetch that follows every add to cart and immediately hide the "added to
  // cart" message.
  useEffect(() => {
    return () => {
      setLoading(false)
      setAddedVariant(null)
      setSelectedVariant(null)
      setSelectedAttrs({})
      setQuantity(1)
    }
  }, [initialId || id, isWholesaleMode])

  useEffect(() => {
    if (selectedAttrs && !_.isEmpty(selectedAttrs)) {
      matchVariantFromSelection(selectedAttrs || {})
    }
  }, [selectedAttrs])

  useEffect(() => {
    setLoading(reflectionQueryResult.loading && !reflectionQueryResult.data)
  }, [reflectionQueryResult])

  useEffect(() => {
    if (addedVariant) {
      const categoryName = maybe<string>(
        () => reflectionQueryResult.data?.reflectionDetails.reflection.categories[0]?.name,
        ''
      )
      const supplyLines: {
        quantity: number
        price: number
      }[] = addedVariant.supplyLinesJson
      const reflection = reflectionQueryResult.data?.reflectionDetails.reflection
      if (reflection) {
        const price = !addedVariant.variant.isSupply
          ? addedVariant.variant.priceRange.discount.min
          : _.sumBy(supplyLines, line => line.quantity * line.price)
        const items: DataLayerItem[] = [
          {
            item_id: addedVariant.variant.sku,
            item_name: reflection.name,
            index: 0,
            item_brand: reflection.product.manufacturer?.name || SITE_NAME,
            item_category: categoryName,
            item_variant: addedVariant.variant.properties
              .map(prop => `${prop.attributeName}: ${prop.valueName}`)
              .join(),
            price,
            quantity,
          },
        ]
        googlePushProductAddToCart({ currency: reflection.product.priceRange.currency, value: price * quantity, items })
      }
    }
  }, [addedVariant])
  const matchVariantFromSelection = async (attrs: { [key: string]: string }) => {
    if (
      Object.values(attrs).find(value => value?.toLowerCase().includes('click to select')) ||
      Object.values(attrs).length < Object.keys(attributes).length
    ) {
      setSelectedVariant(null)
      return
    }
    // setLocalLoading(true)
    void loadProductVariant({
      variables: {
        productPk: reflectionQueryResult.data.reflectionDetails.reflection.product.pk,
        cartType,
        wholesale: isWholesaleMode,
        attrs: Object.entries(attrs).map(([key, val]) => ({
          attrName: key,
          valueName: val,
        })),
      },
    })
  }

  const onChangeQuantity = (val: string | number) => {
    if (+val > 0) {
      setQuantity(+val)
    }
  }

  // The wholesale form keeps its variant after a change: reloaded, it shows the new cart quantity.
  const reloadSelectedVariant = async () => {
    const { data } = await productVariantQueryResult.refetch()
    if (data?.productVariant) {
      setSelectedVariant(data.productVariant)
      setQuantity(data.productVariant.cartQuantity || 1)
    }
  }

  // Runs a wholesale cart change; `change` throws the mutation's errors.
  const changeWholesaleCart = async (change: () => Promise<void>) => {
    setLocalLoading(true)
    try {
      await change()
      await reloadSelectedVariant()
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLocalLoading(false)
  }

  const onAddToWholesaleCart = () =>
    changeWholesaleCart(async () => {
      const { data } = await addVariantToWholesaleCart({ variables: { pk: selectedVariant.pk, quantity } })
      const errors = data?.addVariantToCart.errors || []
      if (errors.length) throw Error(formErrorsToString(errors))
      enqueueSnackbar(t('productCard.addedToCart', 'Added to cart!'), { variant: 'success' })
    })

  const onAddFullKitToWholesaleCart = () =>
    changeWholesaleCart(async () => {
      const cartLinePk = selectedVariant.cartLines?.[0]
      if (selectedVariant.cartQuantity > 0 && cartLinePk) {
        const { data } = await updateWholesaleCartLine({
          variables: { cartLine: cartLinePk, variant: selectedVariant.pk, quantity, cartType },
        })
        const errors = data?.cartUpdateVariant.errors || []
        if (errors.length) throw Error(formErrorsToString(errors))
      } else {
        await addFullKitToBag({ variables: { pk: selectedVariant.pk, quantity, cartType } })
      }
      enqueueSnackbar(t('productCard.addedToCart', 'Added to cart!'), { variant: 'success' })
    })

  const onRemoveFromCart = async () => {
    setRemoving(true)
    try {
      await deleteCartLine({ variables: { variant: selectedVariant.id, cartType } })
      await reloadSelectedVariant()
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setRemoving(false)
  }

  const onAddToBag = async e => {
    e.preventDefault()
    if (isWholesaleMode) return onAddToWholesaleCart()
    setLocalLoading(true)
    try {
      const response = await addVariantToBag({
        variables: {
          pk: selectedVariant.pk,
          quantity: quantity,
        },
      })
      const errors = response.data.addVariantToCart.errors
      if (!errors || _.isEmpty(errors)) {
        setAddedVariant(response.data?.addVariantToCart)
        setLocalLoading(false)
        setSelectedVariant(null)
        setSelectedAttrs({})
        await Promise.all([reflectionQueryResult.refetch()])
      } else {
        enqueueSnackbar(formErrorsToString(errors), {
          variant: 'error',
        })
      }
    } catch (e) {
      enqueueSnackbar(e.message, {
        variant: 'error',
      })
    }
    setLocalLoading(false)
  }
  const onAddToBagFullKit = async e => {
    e.preventDefault()
    if (isWholesaleMode) return onAddFullKitToWholesaleCart()
    setLocalLoading(true)
    try {
      const res = await addFullKitToBag({
        variables: { pk: selectedVariant.pk, quantity },
      })
      setAddedVariant(res.data?.addFullKitToCart)
      setSelectedVariant(null)
      setSelectedAttrs({})
      setLocalLoading(false)
      await Promise.all([reflectionQueryResult.refetch()])
    } catch (e) {
      enqueueSnackbar(e.message, {
        variant: 'error',
      })
    }
    setLocalLoading(false)
  }
  const onCustomizeKit = e => {
    e.preventDefault()
    setLocalLoading(true)
    // Wholesale mode: the form is filled with the kit's parts in the cart, if it's there.
    loadKitData({
      variables: isWholesaleMode
        ? { pk: selectedVariant.pk, cartLine: selectedVariant.cartLines?.[0] ?? null, cartType }
        : { pk: selectedVariant.pk },
    })
  }

  const onSubmitKit = async (supplyLine = null, variants: { pk: number; quantity: number }[], setAsDefault = false) => {
    setKitLoading(true)
    try {
      const response = await addKitToBag({
        variables: {
          pk: selectedVariant.pk,
          variants: variants.map(el => ({
            pk: el.pk,
            quantity: el.quantity,
          })),
          setAsDefault,
          quantity,
          cartType,
        },
      })
      const errors = response.data?.addKitToCart.errors || []
      if (errors.length) {
        throw Error(errors[0].messages[0])
      }
      onCloseKitPanel()
      if (isWholesaleMode) {
        enqueueSnackbar(t('productCard.addedToCart', 'Added to cart!'), { variant: 'success' })
        // Saving a default also changes `hasDefaultKit` and its price.
        await reloadSelectedVariant()
      } else {
        setAddedVariant(response.data?.addKitToCart)
        setLocalLoading(false)
        await Promise.all([reflectionQueryResult.refetch()])
      }
    } catch (e) {
      enqueueSnackbar(e.message, {
        variant: 'error',
      })
    }
    setKitLoading(false)
    onCloseKitPanel()
  }

  const onOpenKitPanel = () => {
    setIsOpenKitPanel(true)
  }
  const onCloseKitPanel = () => {
    setIsOpenKitPanel(false)
    setLocalLoading(false)
  }
  const onGooglePushProductDetails = useCallback(() => {
    const categoryName = maybe<string>(
      () => reflectionQueryResult.data?.reflectionDetails.reflection.categories[0]?.name,
      ''
    )
    console.log(categoryName)
  }, [reflectionQueryResult.data])

  const reflectionAllImages = useMemo(() => {
    return maybe(() => reflectionQueryResult.data.reflectionDetails.reflection.product.images, [])
  }, [reflectionQueryResult.data, selectedVariant])

  const onDownloadFiles = async (url: string) => {
    if (!url) {
      setParams({ global_dialog_form: 'contactUs' }, 'replaceIn')
    } else {
      await downloadFile(
        url,
        () => {
          setLoading(true)
        },
        async () => {
          await reflectionQueryResult.refetch()
          setLoading(false)
        },
        error => {
          setLoading(false)
        },
        (msg, variant) => enqueueSnackbar(msg, { variant })
      )
    }
  }

  const redirectUrl = reflectionQueryResult.data?.reflectionDetails.redirectUrl
    ? new URL(reflectionQueryResult.data?.reflectionDetails.redirectUrl)
    : null

  // The form's prices, quantity limit and cart state, retail or wholesale.
  const product = reflectionQueryResult.data?.reflectionDetails?.reflection?.product
  const wholesalePrice = selectedVariant?.wholesalePrice
  const formState: Pick<
    IProductContext,
    'isWholesale' | 'priceRange' | 'defaultKitPrice' | 'maxQuantity' | 'cartQuantity' | 'removing' | 'onRemoveFromCart'
  > = isWholesaleMode
    ? {
        isWholesale: true,
        priceRange: wholesalePrice
          ? {
              currency: wholesalePrice.currency,
              discount: { min: wholesalePrice.discount, max: wholesalePrice.discount },
              undiscount: { min: wholesalePrice.undiscount, max: wholesalePrice.undiscount },
            }
          : product?.wholesalePriceRange || null,
        defaultKitPrice: selectedVariant?.defaultKitWholesalePrice || null,
        maxQuantity: selectedVariant ? getWholesaleMaxQuantity(selectedVariant) : MAX_WHOLESALE_PRODUCT_QTY,
        cartQuantity: selectedVariant?.cartQuantity || 0,
        removing,
        onRemoveFromCart,
      }
    : {
        isWholesale: false,
        priceRange: selectedVariant?.priceRange || product?.priceRange || null,
        defaultKitPrice: selectedVariant?.defaultKitPrice || null,
        maxQuantity: selectedVariant && !selectedVariant.isSupply ? selectedVariant.stockQuantity : MAX_PRODUCT_QTY,
        cartQuantity: 0,
        removing: false,
        onRemoveFromCart: () => {},
      }

  if (isModal) {
    return (
      <>
        {!!reflectionQueryResult.data?.reflectionDetails?.reflection && (
          <ProductContext.Provider
            value={{
              selectedVariant: selectedVariant,
              reflection: reflectionQueryResult.data?.reflectionDetails?.reflection,
              formValues: {
                quantity,
                selectedAttrs,
              },
              formMethods: {
                onChangeQuantity,
                setSelectedAttrs,
              },
              loading:
                localLoading ||
                productVariantQueryResult.loading ||
                reflectionQueryResult.loading ||
                dataKitQuery.loading,
              me: dataUser.data?.me,
              attributes,
              images: (selectedVariant?.allImages || reflectionAllImages).map(node => ({
                id: node.id,
                alt: node.alt,
                thumbnail: node.thumbnailUrl,
                url: node.url,
                width: node.width,
                height: node.height,
              })),
              lastAddedVariantInfo: addedVariant,
              isModal,

              matchVariantFromSelection,
              onAddToBag,
              onAddToBagFullKit,
              onCustomizeKit,

              setLastAddedVariantInfo: setAddedVariant,
              onDownloadFiles,
              onChangeSelectedVariant: async attrs => {},
              setIsOpenProductDialog,
              ...formState,
            }}
          >
            <Page onGooglePushProductDetails={onGooglePushProductDetails} isModal={isModal} initialId={initialId} />
          </ProductContext.Provider>
        )}
        <VariantKitPanel
          isOpen={isOpenKitPanel}
          onClosePanel={onCloseKitPanel}
          onOpen={onOpenKitPanel}
          onSubmit={onSubmitKit}
          loading={kitLoading}
          data={kitFormData}
          isWholesale={isWholesaleMode}
        />
      </>
    )
  }
  return (
    <>
      {redirectUrl && <Redirect to={redirectUrl.pathname} />}
      {!redirectUrl && !reflectionQueryResult.loading && !reflectionQueryResult.data?.reflectionDetails.reflection && (
        <Page404 />
      )}
      {!!reflectionQueryResult.data?.reflectionDetails?.reflection && (
        <MetaWrapper
          meta={extractReflectionMeta(
            reflectionQueryResult.data?.reflectionDetails,
            selectedVariant?.allImages || reflectionAllImages
          )}
          titleDetails={
            reflectionQueryResult.data?.reflectionDetails?.reflection?.product?.name ||
            reflectionQueryResult.data?.reflectionDetails?.reflection?.name ||
            ''
          }
        >
          <ProductContext.Provider
            value={{
              selectedVariant: selectedVariant,
              reflection: reflectionQueryResult.data?.reflectionDetails?.reflection,
              formValues: {
                quantity,
                selectedAttrs,
              },
              formMethods: {
                onChangeQuantity,
                setSelectedAttrs,
              },
              loading: localLoading || reflectionQueryResult.loading || dataKitQuery.loading,
              me: dataUser.data?.me,
              attributes,
              images: (selectedVariant?.allImages || reflectionAllImages).map(node => ({
                id: node.id,
                alt: node.alt,
                thumbnail: node.thumbnailUrl,
                url: node.url,
                width: node.width,
                height: node.height,
              })),
              lastAddedVariantInfo: addedVariant,

              matchVariantFromSelection,
              onAddToBag,
              onAddToBagFullKit,
              onCustomizeKit,

              setLastAddedVariantInfo: setAddedVariant,
              onDownloadFiles,
              onChangeSelectedVariant: async attrs => {},
              ...formState,
            }}
          >
            <Page onGooglePushProductDetails={onGooglePushProductDetails} isModal={isModal} initialId={initialId} />
          </ProductContext.Provider>
        </MetaWrapper>
      )}
      <VariantKitPanel
        isOpen={isOpenKitPanel}
        onClosePanel={onCloseKitPanel}
        onOpen={onOpenKitPanel}
        onSubmit={onSubmitKit}
        loading={kitLoading}
        data={kitFormData}
        isWholesale={isWholesaleMode}
      />
    </>
  )
}

export default React.memo(View)
